import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  LoaderCircle,
  Plus,
  RefreshCw,
  UtensilsCrossed,
} from "lucide-react";

import AdminLayout from "../components/AdminLayout";
import ConfirmDialog from "../components/ConfirmDialog";
import EmptyState from "../components/EmptyState";

import MenuItemCard from "../menu/MenuItemCard";
import MenuItemForm from "../menu/MenuItemForm";
import MenuToolbar from "../menu/MenuToolbar";

import {
  createMenuItem,
  deleteMenuItem,
  fetchMenuCategories,
  fetchMenuItem,
  fetchMenuItems,
  syncMenuItemAddOns,
  syncMenuItemFlavors,
  syncMenuItemVariants,
  toggleMenuItemActive,
  toggleMenuItemAvailable,
  toggleMenuItemFeatured,
  updateMenuItem,
} from "../menu/menuApi";

export default function AdminMenuPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] =
    useState("");
  const [category, setCategory] = useState("");
  const [active, setActive] = useState("");
  const [ordering, setOrdering] =
    useState("display_order");
  const [viewMode, setViewMode] = useState("grid");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] =
    useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openMenuItemId, setOpenMenuItemId] = useState(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const loadCategories = useCallback(async (signal) => {
    try {
      const data = await fetchMenuCategories({ signal });
      setCategories(data);
    } catch (requestError) {
      if (requestError.name !== "AbortError") {
        setError(
          requestError.message ||
            "Categories could not be loaded.",
        );
      }
    }
  }, []);

  const loadMenuItems = useCallback(
    async (signal) => {
      setIsLoading(true);
      setError("");

      try {
        const data = await fetchMenuItems({
          search: debouncedSearch,
          category,
          active,
          ordering,
          signal,
        });

        setMenuItems(data);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(
            requestError.message ||
              "The menu could not be loaded.",
          );
        }
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      }
    },
    [
      active,
      category,
      debouncedSearch,
      ordering,
    ],
  );

  useEffect(() => {
    const controller = new AbortController();

    loadCategories(controller.signal);

    return () => controller.abort();
  }, [loadCategories]);

  useEffect(() => {
    const controller = new AbortController();

    loadMenuItems(controller.signal);

    return () => controller.abort();
  }, [loadMenuItems]);

  useEffect(() => {
    const itemId = searchParams.get("item");
    if (!itemId) return undefined;
    let alive = true;
    const controller = new AbortController();
    fetchMenuItem(itemId, { signal: controller.signal })
      .then((item) => {
        if (!alive) return;
        setSelectedItem(item);
        setIsFormOpen(true);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message || "Menu item could not be loaded.");
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [searchParams]);

  function openCreateForm() {
    setSelectedItem(null);
    setIsFormOpen(true);
  }

  function openEditForm(item) {
    setOpenMenuItemId(null);
    setSelectedItem(item);
    setIsFormOpen(true);
  }

  function closeForm() {
    if (isSaving) {
      return;
    }

    setIsFormOpen(false);
    setSelectedItem(null);
    if (searchParams.get("item")) setSearchParams({}, { replace: true });
  }

  async function handleFormSubmit(savePayload) {
    setIsSaving(true);
    setError("");

    try {
      let savedItem;

      if (selectedItem?.id) {
        savedItem = await updateMenuItem(
          selectedItem.id,
          savePayload.menuItemFormData,
        );
      } else {
        savedItem = await createMenuItem(
          savePayload.menuItemFormData,
        );
      }

      await syncMenuItemVariants(
        savedItem.id,
        savePayload.variants,
      );
      await syncMenuItemFlavors(
        savedItem.id,
        savePayload.flavors,
      );
      await syncMenuItemAddOns(
        savedItem.id,
        savePayload.add_ons,
      );

      const finalItem = await fetchMenuItem(savedItem.id);

      setMenuItems((current) => {
        const exists = current.some(
          (item) => item.id === finalItem.id,
        );

        if (exists) {
          return current.map((item) =>
            item.id === finalItem.id ? finalItem : item,
          );
        }

        return [finalItem, ...current];
      });

      setSuccessMessage(
        `${finalItem.name} was ${
          selectedItem?.id ? "updated" : "added to the menu"
        }.`
      );

      setIsFormOpen(false);
      setSelectedItem(null);

      window.setTimeout(() => {
        setSuccessMessage("");
      }, 4000);

      return finalItem;
    } catch (requestError) {
      setError(
        requestError.message ||
          "The menu item could not be saved.",
      );

      throw requestError;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleActive(item) {
    setError("");

    try {
      const updatedItem =
        await toggleMenuItemActive(item.id);

      setMenuItems((current) =>
        current.map((currentItem) =>
          currentItem.id === updatedItem.id
            ? updatedItem
            : currentItem,
        ),
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          "The item visibility could not be changed.",
      );
    }
  }

  async function handleToggleFeatured(item) {
    setError("");

    try {
      const updatedItem =
        await toggleMenuItemFeatured(item.id);

      setMenuItems((current) =>
        current.map((currentItem) =>
          currentItem.id === updatedItem.id
            ? updatedItem
            : currentItem,
        ),
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          "The featured status could not be changed.",
      );
    }
  }

  async function handleToggleAvailable(item) {
    setError("");

    try {
      const updatedItem =
        await toggleMenuItemAvailable(item.id);

      setMenuItems((current) =>
        current.map((currentItem) =>
          currentItem.id === updatedItem.id
            ? updatedItem
            : currentItem,
        ),
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          "The availability could not be changed.",
      );
    }
  }

  async function handleDelete() {
    if (!itemToDelete) {
      return;
    }

    setIsDeleting(true);
    setError("");

    try {
      await deleteMenuItem(itemToDelete.id);

      setMenuItems((current) =>
        current.filter(
          (item) => item.id !== itemToDelete.id,
        ),
      );

      setSuccessMessage(
        `${itemToDelete.name} was deleted.`,
      );

      setItemToDelete(null);

      window.setTimeout(() => {
        setSuccessMessage("");
      }, 4000);
    } catch (requestError) {
      setError(
        requestError.message ||
          "The menu item could not be deleted.",
      );
    } finally {
      setIsDeleting(false);
    }
  }

  const hasFilters =
    Boolean(search) ||
    Boolean(category) ||
    active !== "";

  function clearFilters() {
    setSearch("");
    setCategory("");
    setActive("");
    setOrdering("display_order");
  }

  return (
    <AdminLayout>
      <div className="admin-page admin-menu-page">
        <section className="admin-page-heading">
          <div>
            <p className="eyebrow">Store management</p>
            <h1>Menu</h1>
            <p>
              Add products, update prices, manage
              availability, and organize the bakery menu.
            </p>
          </div>

          <button
            type="button"
            className="btn btn--primary"
            onClick={openCreateForm}
          >
            <Plus size={18} />
            Add Menu Item
          </button>
        </section>

        {successMessage && (
          <div className="alert alert--success">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="admin-menu-error">
            <AlertCircle size={19} />

            <div>
              <strong>Something went wrong</strong>
              <span>{error}</span>
            </div>

            <button
              type="button"
              className="admin-link-button"
              onClick={() => loadMenuItems()}
            >
              <RefreshCw size={15} />
              Try again
            </button>
          </div>
        )}

        <MenuToolbar
          search={search}
          category={category}
          active={active}
          ordering={ordering}
          viewMode={viewMode}
          categories={categories}
          onSearchChange={setSearch}
          onCategoryChange={setCategory}
          onActiveChange={setActive}
          onOrderingChange={setOrdering}
          onViewModeChange={setViewMode}
        />

        <section className="admin-menu-summary">
          <div>
            <strong>{menuItems.length}</strong>
            <span>
              {menuItems.length === 1
                ? "menu item"
                : "menu items"}
            </span>
          </div>
        </section>

        {isLoading ? (
          <div className="admin-menu-loading">
            <LoaderCircle
              size={34}
              className="admin-spin"
            />
            <p>Loading menu items...</p>
          </div>
        ) : menuItems.length === 0 ? (
          <EmptyState
            icon={UtensilsCrossed}
            title={
              hasFilters
                ? "No matching menu items"
                : "Your menu is empty"
            }
            description={
              hasFilters
                ? "Try changing your search or clearing the current filters."
                : "Add the first bakery item to begin building the online menu."
            }
            actionLabel={
              hasFilters
                ? "Clear Filters"
                : "Add Menu Item"
            }
            onAction={
              hasFilters ? clearFilters : openCreateForm
            }
          />
        ) : (
          <div
            className={[
              "admin-menu-items",
              viewMode === "list"
                ? "admin-menu-items--list"
                : "admin-menu-items--grid",
            ].join(" ")}
          >
            {menuItems.map((item) => (
              <MenuItemCard
                key={item.id}
                item={item}
                isMenuOpen={openMenuItemId === item.id}
                onToggleMenu={() =>
                  setOpenMenuItemId((current) =>
                    current === item.id ? null : item.id,
                  )
                }
                onCloseMenu={() => setOpenMenuItemId(null)}
                onEdit={openEditForm}
                onDelete={(menuItem) => {
                  setOpenMenuItemId(null);
                  setItemToDelete(menuItem);
                }}
                onToggleActive={handleToggleActive}
                onToggleAvailable={handleToggleAvailable}
                onToggleFeatured={handleToggleFeatured}
              />
            ))}
          </div>
        )}
      </div>

      <MenuItemForm
        item={selectedItem}
        categories={categories}
        isOpen={isFormOpen}
        isSaving={isSaving}
        onClose={closeForm}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDialog
        isOpen={Boolean(itemToDelete)}
        title="Delete menu item?"
        description={
          itemToDelete
            ? `${itemToDelete.name} will be permanently removed from the menu. This action cannot be undone.`
            : ""
        }
        confirmLabel="Delete Item"
        isWorking={isDeleting}
        onCancel={() => {
          if (!isDeleting) {
            setItemToDelete(null);
          }
        }}
        onConfirm={handleDelete}
      />
    </AdminLayout>
  );
}